import { useCallback, useEffect, useMemo, useState } from 'react';

const LAST_SHOWN_KEY = 'install-hint:last-shown';
const THROTTLE_MS = 24 * 60 * 60 * 1000; // show automatically at most once per day
const MOBILE_QUERY = '(max-width: 767px)';
const OPEN_EVENT = 'revvly:open-install-hint';

export type InstallPlatform = 'ios' | 'android' | 'other';

/** The non-standard event Chromium fires when the app is installable. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari exposes standalone on navigator, not via display-mode.
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function detectPlatform(): InstallPlatform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  // Desktop-mode iPadOS Safari reports as "MacIntel" with a touch screen, so
  // the UA test above misses it — treat it as iOS so it gets the install steps.
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 0) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'other';
}

export interface UseInstallPromptResult {
  open: boolean;
  setOpen: (open: boolean) => void;
  platform: InstallPlatform;
  /** True once Chromium's beforeinstallprompt has been captured (Android/desktop Chrome). */
  canPromptInstall: boolean;
  /** Trigger the native install prompt (no-op when not available). */
  promptInstall: () => Promise<void>;
  /** Already running as an installed PWA — nothing to prompt. */
  installed: boolean;
}

/**
 * Drives the mobile "install to home screen" hint. Auto-opens on mobile web
 * (when not already installed) at most once per 24h, and can be opened on
 * demand via {@link openInstallHint}. Captures Chromium's beforeinstallprompt
 * for a one-tap install; iOS Safari (no such event) falls back to manual steps.
 */
export function useInstallPrompt(): UseInstallPromptResult {
  const [open, setOpen] = useState(false);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const platform = useMemo(detectPlatform, []);
  const installed = isStandalone();

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event): void => {
      // Suppress the mini-infobar so we can present our own hint.
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = (): void => {
      setDeferred(null);
      setOpen(false);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // On-demand open (e.g. from the notifications setting).
  useEffect(() => {
    const onOpen = (): void => setOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  // Auto-show: mobile web, not installed, and only if a hint is actionable
  // (iOS manual steps, or a captured Android prompt). Throttled to once/24h.
  const actionable = !installed && (platform === 'ios' || deferred !== null);
  useEffect(() => {
    if (!actionable) return;
    if (!window.matchMedia(MOBILE_QUERY).matches) return;
    let lastShown = 0;
    try {
      lastShown = Number(localStorage.getItem(LAST_SHOWN_KEY)) || 0;
    } catch {
      lastShown = 0;
    }
    if (Date.now() - lastShown < THROTTLE_MS) return;
    setOpen(true);
    try {
      localStorage.setItem(LAST_SHOWN_KEY, String(Date.now()));
    } catch {
      /* localStorage unavailable — still show this session */
    }
  }, [actionable]);

  const promptInstall = useCallback(async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice.catch(() => undefined);
    setDeferred(null);
    setOpen(false);
  }, [deferred]);

  return {
    open,
    setOpen,
    platform,
    canPromptInstall: deferred !== null,
    promptInstall,
    installed,
  };
}

/** Open the install hint on demand from anywhere in the app. */
export function openInstallHint(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(OPEN_EVENT));
  }
}

/** Whether the app is already running as an installed PWA (standalone). */
export function isAppInstalled(): boolean {
  return isStandalone();
}

/** Whether the viewport is phone-sized (matches the install hint's auto-show gate). */
export function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches;
}
